import React, { useEffect, useState, useRef } from 'react'
import { debounce } from 'lodash'
import { useCookies } from 'react-cookie'
import shortid from 'shortid'

import { store } from 'app-redux/store'
import { useActionless, useAppSelector } from 'app-redux/hooks'

import CardService from 'services/card.service'
import ToastService from 'services/toast.service'
import SaveService from 'services/save.service'
import PinService from 'services/pin.service'

import { CardType, GAME_IDS, OWNED, GameType } from 'types/deck'
import { PRICE_CODES } from 'types/active'
import { Option } from 'components/elements/ArchidektDropdown/types'
import { DOUBLE_FACED_LAYOUTS } from 'types/card'
import { SET_CACHEABLES_STATE } from 'app-redux/cacheables/actions/types'

import Icon from 'components/elements/Icon'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import PhatButton from 'components/formElements/PhatButton'
import PhatInput from 'components/formElements/PhatInput'
import Checkbox from 'components/formElements/Checkbox'
import PhatDropdown from 'components/formElements/PhatDropdown'
import BasicCard from 'components/card/BasicCard'
import ContextMenu from 'components/elements/ContextMenu'
import Prices from 'components/card/controlsAndInfo/prices'
import LineLoader from 'components/elements/LineLoader'
import ImageSizeControls from 'components/collectionV2/views/Image/ImageSizeControls'

import { NextPrevOptions } from 'components/elements/Overlay/CardDetailsOverlay'
import { GridSizeClassNames } from 'components/cardSearchPanel/cardLayouts/cardResultsGrid'

import styles from './cardEditionsGridOverlay.module.scss'

const cacheKey = 'edition-order-by-v2'

type Props = {
  open: boolean
  onClose: () => void
  card: CardType
  onCardSelected: (card: CardType, skipGoBackOnClick?: boolean) => void

  overrideGame?: GameType
  nextPrevControls?: {
    previous: NextPrevOptions
    next: NextPrevOptions
  }
}

const CardEditionsGridOverlay = ({ open, onClose, card, onCardSelected, overrideGame, nextPrevControls }: Props) => {
  const [setCachablesState] = useActionless(SET_CACHEABLES_STATE)

  const searchImageSize = useAppSelector(state => state.cacheables.searchImageSize)

  const [loading, setLoading] = useState(true)
  const [results, setResults] = useState(new Array<CardType>())
  const [next, setNext] = useState<string | null>(null)
  const [lastFetched, setLastFetched] = useState<number | string | null>(null)
  const [limitToGameType, setLimitToGameType] = useState(true)
  const [limitToMyCollection, setLimitToMyCollection] = useState(false)

  const [{ [cacheKey]: orderBy = 'released_at' }, setCookies] = useCookies([cacheKey])

  const gameId = useAppSelector(state => state.deck.game)
  const game = overrideGame || gameId

  useEffect(() => {
    if (!open) return

    // Collaborative editng the card map causes a re-render of the card, and we don't want it to refetch unless it needs to
    if (lastFetched !== card.oracleCardId) handleSearch(card.oracleCardId)
  }, [card, open])

  useEffect(() => {
    if (!open) return

    handleSearch(card.oracleCardId)
  }, [limitToGameType, limitToMyCollection, orderBy])

  const handleSearch = (oracleCardId: string | number, editionSearch = '') => {
    if (!oracleCardId) return

    setLoading(true)
    setResults([])

    let actualOrderBy = '-released_at'

    if (orderBy === 'released_at') actualOrderBy = '-released_at'
    if (orderBy === 'editiondate') actualOrderBy = '-editiondate'
    if (orderBy === 'editionname') actualOrderBy = 'editionname'

    if (orderBy === 'price') {
      const priceSource = store.getState().active.priceSource[0]
      const priceCode = PRICE_CODES[priceSource] || 'ck'

      actualOrderBy = `prices__${priceCode}`
    }

    CardService.list({
      oracleCardIds: oracleCardId,
      editionSearch,
      includeDigital: true,
      includeArtCards: true,
      includeEmblems: true,
      includeTokens: true,
      exact: true,
      allEditions: true,
      orderBy: actualOrderBy,
      game: limitToGameType ? game : null,
      collection: limitToMyCollection ? 1 : undefined,
    })
      .then(res => {
        setResults(res.cards)
        setNext(res.next)
        setLastFetched(oracleCardId)
      })
      .catch(err => {
        console.error(err)

        ToastService.create('Unable to fetch card editions', 'error')
      })
      .finally(() => setLoading(false))
  }

  const debouncedSeach = useRef(debounce(handleSearch, 500)).current

  const handleSearchChange = (query: string) => {
    setLoading(true)

    debouncedSeach(card.oracleCardId, query)
  }

  const handleLoadMore = () => {
    setLoading(true)

    CardService.list({}, next)
      .then(res => {
        setResults([...results, ...res.cards])
        setNext(res.next)
      })
      .catch(err => {
        console.error(err)

        ToastService.create('Unable to fetch card editions', 'error')
      })
      .finally(() => setLoading(false))
  }

  const handleSelect = (updatedCard: CardType, skipGoBackOnClick?: boolean) => {
    onCardSelected(
      {
        ...updatedCard,
        createdAt: card.createdAt,
        deckRelationId: card.deckRelationId,
        qty: card.qty,
        categories: card.categories,
        customCmc: card.customCmc,
        colorLabel: card.colorLabel,
        id: card.id,
        modifier: updatedCard.options.includes(card.modifier) ? card.modifier : updatedCard.options[0],
      },
      skipGoBackOnClick,
    )
  }

  const handleOnClose = () => {
    onClose()
  }

  const handleSetOrderBy = (orderBy: string) => {
    const oneYearFromNow = new Date(new Date().setFullYear(new Date().getFullYear() + 1))

    setCookies(cacheKey, orderBy, { path: '/', expires: oneYearFromNow })
  }

  const nextPrevButtonGroup = nextPrevControls ? (
    <>
      <button
        className={styles.nextPrevButton}
        onClick={nextPrevControls.previous.onClick}
        disabled={nextPrevControls.previous.disabled}>
        <Icon name="arrow left" /> <span>{nextPrevControls.previous.label}</span>
      </button>

      <button
        className={styles.nextPrevButton}
        onClick={nextPrevControls.next.onClick}
        disabled={nextPrevControls.next.disabled}>
        <span>{nextPrevControls.next.label}</span>
        <Icon name="arrow right" />
      </button>
    </>
  ) : null

  const gameTypeName = game ? GAME_IDS[game] : null
  const gridClassName = GridSizeClassNames[searchImageSize]

  return (
    <GlobalOverlayStack
      open={open}
      onClose={handleOnClose}
      className={styles.container}
      mobileToolbarOptions={[
        { type: 'blank' },
        {
          icon: 'arrow circle left',
          label: nextPrevControls?.previous.label,
          onClick: nextPrevControls?.previous.onClick,
          disabled: nextPrevControls?.previous.disabled,
        },
        {
          icon: 'arrow circle right',
          label: nextPrevControls?.next.label,
          onClick: nextPrevControls?.next.onClick,
          disabled: nextPrevControls?.next.disabled,
        },
      ]}>
      <div className={styles.content}>
        <h2>Select a printing</h2>

        <div className={styles.controls}>
          <div className={styles.formElements}>
            <PhatInput
              header="Search printings..."
              selectOnFocus
              key={card.id}
              className={styles.input}
              placeholder="Filter set name"
              onChange={handleSearchChange}
            />
            <PhatDropdown
              value={orderBy}
              header="Order by"
              options={[
                {
                  label: 'Release date',
                  id: 'released_at',
                  onClick: () => handleSetOrderBy('released_at'),
                },
                {
                  label: 'Edition date',
                  id: 'editiondate',
                  onClick: () => handleSetOrderBy('editiondate'),
                },
                {
                  label: 'Set Name',
                  id: 'editionname',
                  onClick: () => handleSetOrderBy('editionname'),
                },
                {
                  label: 'Price',
                  id: 'price',
                  onClick: () => handleSetOrderBy('price'),
                },
              ]}
            />
          </div>

          {!!nextPrevControls && <div className={styles.nextPrevContainer}>{nextPrevButtonGroup}</div>}
        </div>

        {!!game && (
          <div className={styles.checkboxContainer}>
            <Checkbox
              className={styles.checkbox}
              label={`Limit to ${gameTypeName} cards`}
              checked={limitToGameType}
              onChange={setLimitToGameType}
            />

            <Checkbox
              className={styles.checkbox}
              label={`Limit to cards in my collection`}
              checked={limitToMyCollection}
              onChange={setLimitToMyCollection}
            />
          </div>
        )}

        <ImageSizeControls
          className={styles.imageSizeControls}
          imageSize={searchImageSize}
          onChange={searchImageSize => setCachablesState({ searchImageSize })}
        />

        <div className={styles.queryableContainer}>
          {loading && <LineLoader fixed />}

          <div className={`${styles.cardGrid} ${gridClassName}`}>
            {results.map(editionCard => (
              <EditionGridCard
                key={editionCard.cardId}
                card={editionCard}
                selected={card.uid === editionCard.uid}
                onCardClicked={handleSelect}
                nextPrevControls={nextPrevControls}
              />
            ))}
          </div>
        </div>
        {next && !loading && (
          <div className={styles.center}>
            <PhatButton onClick={handleLoadMore}>Load more</PhatButton>
          </div>
        )}
      </div>
    </GlobalOverlayStack>
  )
}

export default CardEditionsGridOverlay

type EditionGridCardProps = {
  card: CardType
  selected: boolean
  onCardClicked: (card: CardType, skipGoBackOnClick?: boolean) => void
  nextPrevControls?: {
    previous: NextPrevOptions
    next: NextPrevOptions
  }
}

const EditionGridCard = ({ card, selected, onCardClicked, nextPrevControls }: EditionGridCardProps) => {
  const [flipped, setFlipped] = useState(false)

  const handleHardAddNewEdition = (card: CardType) =>
    SaveService.save({ ...card, id: shortid.generate(), deckRelationId: '', qty: 1 })

  const handlePinCard = (card: CardType) =>
    PinService.add({ ...card, id: shortid.generate(), deckRelationId: '', qty: 1 })
      .then(() =>
        ToastService.create(`${card.name} (${card.setCode}) ${card.collectorNumber} pinned`, 'Pinned cards', 'success'),
      )
      .catch(() => ToastService.create('Unable to create pin', 'Pinned cards', 'error'))

  // prettier-ignore
  const switchToEdition = (<><Icon name="arrow right" /> Switch to edition</>)
  // prettier-ignore
  const addNewEdition = (<><Icon name="add" /> Add as new card</>)
  // prettier-ignore
  const pinThisEdition = (<><Icon name="pin" /> {card.pinnedStatus === OWNED.PRINTING ? 'Add as new pin' : 'Pin this card'}</>)

  const options: Option[] = [
    { label: switchToEdition, onClick: () => onCardClicked(card) },
    { label: addNewEdition, onClick: () => handleHardAddNewEdition(card) },
    { label: pinThisEdition, onClick: () => handlePinCard(card) },
    { type: 'spacer' },
    {
      label: 'Select and goto next',
      onClick: () => {
        onCardClicked(card, true)

        if (nextPrevControls?.next.onClick) nextPrevControls.next.onClick()
      },
      icon: 'arrow circle right',
    },
    {
      label: 'Select and goto previous',
      onClick: () => {
        onCardClicked(card, true)

        if (nextPrevControls?.previous.onClick) nextPrevControls.previous.onClick()
      },
      icon: 'arrow circle left',
    },
  ]

  return (
    <div className={styles.cardContainer}>
      <ContextMenu menuClassName={styles.contextMenu} options={options} disableOnTouch>
        <div className={styles.cardBorderContainer} title={`${card.name} (${card.setCode}) ${card.collectorNumber}`}>
          {selected && <div className={styles.currentlySelectedCardBorder}>Selected printing</div>}
          <button className={styles.cardButton} onClick={() => onCardClicked(card)}>
            <BasicCard className={styles.basicCard} card={card} includeCorderOwned flipped={flipped} />
          </button>

          {DOUBLE_FACED_LAYOUTS.includes(card.layout) && (
            <button className={styles.flipButton} onClick={() => setFlipped(!flipped)}>
              <Icon name="refresh" />
            </button>
          )}
        </div>
      </ContextMenu>
      <div className={styles.extrasContainer}>
        <div className={styles.extras}>
          <Prices card={card} />

          <ContextMenu menuClassName={styles.extrasMenu} options={options} openOnLeftClick>
            <button className={styles.extrasTrigger}>
              <Icon name="ellipsis horizontal" />
            </button>
          </ContextMenu>
        </div>
      </div>
    </div>
  )
}
